/**
 * Markdown-it plugin for rendering secret fields in preview.
 * SECURITY: Never exposes actual secret content — only shows a lock icon + label.
 *
 * Strategy: run a core rule AFTER block/inline parsing to walk the token
 * stream and replace any inline `text` token containing a :secret[…]{…}
 * directive with an html_inline token. This avoids fighting markdown-it's
 * own escape / emphasis rules that run before custom inline rules.
 */

import type MarkdownIt from 'markdown-it';
import type Token from 'markdown-it/lib/token.mjs';


export function secretFieldPlugin(md: MarkdownIt): void {
  md.core.ruler.push('secret_field_tokens', (state) => {
    for (let i = 0; i < state.tokens.length; i++) {
      const blockToken = state.tokens[i];
      if (blockToken.type !== 'inline' || !blockToken.children) continue;

      const newChildren: Token[] = [];

      for (const child of blockToken.children) {
        if (child.type !== 'text' && child.type !== 'softbreak') {
          newChildren.push(child);
          continue;
        }

        if (child.type === 'softbreak') {
          newChildren.push(child);
          continue;
        }

        // child.type === 'text' — scan for :secret[...]{...}
        const src = child.content;
        const re = new RegExp(':secret\\[((?:[^\\\\[\\]]|\\\\.)*)](?:\\{([^}]*)})?' , 'g');
        let last = 0;
        let matched = false;
        let m: RegExpExecArray | null;

        while ((m = re.exec(src)) !== null) {
          matched = true;

          // Text before the match
          if (m.index > last) {
            const pre = new state.Token('text', '', 0);
            pre.content = src.slice(last, m.index);
            newChildren.push(pre);
          }

          // Secret field token
          const attrs = m[2] ?? '';
          const label = extractLabel(attrs);
          const htmlTok = new state.Token('html_inline', '', 0);
          htmlTok.content = buildInline(label);
          newChildren.push(htmlTok);

          last = m.index + m[0].length;
        }

        if (!matched) {
          // No secret directive found — keep original token unchanged
          newChildren.push(child);
          continue;
        }

        // Remaining text after last match
        if (last < src.length) {
          const post = new state.Token('text', '', 0);
          post.content = src.slice(last);
          newChildren.push(post);
        }
      }

      blockToken.children = newChildren;
    }

    return true;
  });
}

function buildInline(label: string): string {
  const labelHtml = label
    ? `<span class="secret-label">${escapeHtml(label)}</span>`
    : '';
  return (
    `<span class="secret-field-inline">` +
    `<svg class="secret-icon" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">` +
    `<path d="M12 17a2 2 0 0 0 2-2 2 2 0 0 0-2-2 2 2 0 0 0-2 2 2 2 0 0 0 2 2` +
    `m6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1` +
    `V6a5 5 0 0 1 5-5 5 5 0 0 1 5 5v2h1m-6-5a3 3 0 0 0-3 3v2h6V6a3 3 0 0 0-3-3z"/>` +
    `</svg>` +
    labelHtml +
    `<span class="secret-placeholder">\u25cf\u25cf\u25cf</span>` +
    `</span>`
  );
}

function extractLabel(attrs: string): string {
  const idx = attrs.indexOf('label=');
  if (idx === -1) return '';
  const rest = attrs.slice(idx + 'label='.length);
  // rest starts with " or '
  const quote = rest[0];
  const end = rest.indexOf(quote, 1);
  return rest.slice(1, end);
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[c] ?? c)
  );
}
